import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { MapsStore } from '../maps.store';
import { SessionStore } from '../session.store';
import { AgenturListUiStore } from './agt.list.ui.store';
import { AgtUserUiStore } from './agt.user.ui.store';
import { ISelectOpt } from 'app/components/common/Select';
import _ from 'lodash';
import { ConvexHullGrahamScan } from 'app/utils/convex.hull.graham.scan';
import { AgenturModel, AgtType } from 'app/models/agentur.model';
import colors from 'assets/scss/colors.module.scss';
import { DvsSeriesOption } from 'app/components/maps/EchartMap';

export const mapColors: string[] = [
	'#0077C8',
	'#F2A900',
	'#1E8927',
	'#E15200',
	'#7C6394',
	'#FF934F',
	'#158390',
	'#FFE8B0',
	'#FCD25B',
	'#F7C7C3',
	'#E4003A',
	'#CCDD61',
	'#916836',
	'#B1DADD',
	'#5B5D30',
	'#B71E3F',
	'#5A3982',
	'#49648C',
	'#96DCFA',
	'#407D71',
	'#5A5360',
];
const mapRedFill = '#ED493F';

export class GroupedAgtVM {
	constructor(agts: AgenturModel[]) {
		this.agts = agts;
	}
	agts: AgenturModel[] = [];

	@computed
	get count() {
		return this.agts.length;
	}

	@computed
	get lat() {
		return this.agts[0].lat!;
	}

	@computed
	get lng() {
		return this.agts[0].lng!;
	}
}

export function encodeSVG(rawSvgString: string): string {
	const symbols = /[\r\n%#()<>?\[\\\]^`{|}]/g;

	// Use single quotes instead of double to avoid URI encoding
	rawSvgString = rawSvgString
		.replace(/'/g, '"')
		.replace(/>\s+</g, '><')
		.replace(/\s{2,}/g, ' ');

	return 'data:image/svg+xml;utf-8,' + rawSvgString.replace(symbols, encodeURIComponent);
}

export class BaseMapLegend implements ISelectOpt {
	constructor(opts: ISelectOpt[]) {
		this.opts = opts;
	}
	opts: ISelectOpt[];
	label = 'Standard';
	value = 'standard';
	getOptValue = (agentur: AgenturModel) => {
		return agentur.gsId;
	};
	getOpt = (agentur: AgenturModel) => {
		const optVal = this.getOptValue(agentur);
		return this.opts.find((o) => o.value === optVal);
	};
	getIndex = (agentur: AgenturModel) => {
		const optVal = this.getOptValue(agentur);
		return this.opts.findIndex((o) => o.value === optVal);
	};
	getColorByIndex = (indx: number) => {
		if (this.value === 'standard') {
			return mapRedFill;
		}

		// Handle invalid indices (e.g., when segment is undefined/null)
		if (indx < 0 || indx >= mapColors.length) {
			return colors.greyLight;
		}

		return mapColors[indx];
	};
	getSvgByIndx = (indx: number) => {
		const color = this.getColorByIndex(indx);

		return `<svg width="26" height="35" viewBox="0 0 26 35" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M12.0675 34.0142L12.0626 34.0066L12.0575 33.9992C10.4619 31.6842 9.08054 29.7074 7.88538 27.997C5.36668 24.3925 3.67481 21.9712 2.54628 20.0626C1.72038 18.6658 1.21366 17.571 0.910149 16.5128C0.607527 15.4576 0.5 14.4145 0.5 13.1122C0.5 6.14355 6.03708 0.5 13 0.5C19.8969 0.5 25.5 6.14525 25.5 13.1122C25.5 14.4132 25.3885 15.4556 25.08 16.5102C24.7705 17.5684 24.2556 18.6633 23.421 20.0607C22.3202 21.9038 20.6951 24.2235 18.3129 27.624C17.0558 29.4185 15.5878 31.514 13.8748 33.9992L13.8697 34.0066L13.8648 34.0142C13.452 34.6619 12.4803 34.6619 12.0675 34.0142Z" 
		fill="${color}" stroke="#000000" stroke-opacity="0.2"/>		
		<circle cx="13" cy="13" r="4.5" fill="black" fill-opacity="0.5"/>
		</svg>`;
	};
	@computed
	get legendDescription() {
		const desc: ISelectOpt[] = [];
		this.opts.forEach((element, indx) => {
			const label = element.label;
			const svg = this.getSvgByIndx(indx);
			desc.push({
				label,
				value: svg,
			});
		});
		return desc;
	}
}

export class GsMapLegend extends BaseMapLegend {
	constructor(opts: ISelectOpt[]) {
		super(opts);
	}
	label = 'GS';
	value = 'gs';
	getOptValue = (agentur: AgenturModel) => {
		return agentur.gsId;
	};
}

export class SegmentierungMapLegend extends BaseMapLegend {
	constructor(opts: ISelectOpt[]) {
		super(opts);
	}
	label = 'Segmentierung';
	value = 'seg';
	getOptValue = (agentur: AgenturModel) => {
		// Handle undefined segment data - return empty string which won't match any option
		const segment = agentur.segment;
		return segment || '';
	};
}

export class EdpMapLegend extends BaseMapLegend {
	constructor(opts: ISelectOpt[]) {
		super(opts);
	}
	label = 'EDP';
	value = 'edp';
	getOptValue = (agentur: AgenturModel) => {
		const u = agentur.users.find((u) => u.isEDP);
		if (u) {
			return u.bnrId.toString();
		}
		return '';
	};
}

export const DefaultLegend = new BaseMapLegend([]);

export class MapPointGroup {
	lat: number;
	lng: number;
	points: MapPoint[] = [];
	constructor(lat: number, lng: number) {
		this.lat = lat;
		this.lng = lng;
	}

	@computed
	get asArray() {
		if (!this.lat || !this.lng) {
			return undefined;
		}
		const x = [this.lng, this.lat, this.points.length, this.value];
		return x;
	}

	@computed
	get value() {
		return _.sumBy(this.points, 'value');
	}
}

export class MapPoint {
	agt: AgenturModel | AgenturModel;
	value: number;
	constructor(agt: AgenturModel | AgenturModel, value: number) {
		this.agt = agt;
		this.value = value;
	}

	@computed
	get hasCoordinates() {
		return this.agt.lat !== 0 && this.agt.lng !== 0;
	}

	@computed
	get lat() {
		return this.agt.lat;
	}
	@computed
	get lng() {
		return this.agt.lng;
	}

	@computed
	get asArray() {
		if (!this.lat || !this.lng) {
			return undefined;
		}
		const x = [this.lng, this.lat, this.agt.agtId, this.value];

		return x;
		// return this.agt.mapBaseData!.concat(this.value);
	}
}

export class AgenturMapsUiStore {
	session: SessionStore;
	mapsStore: MapsStore;
	@observable
	agenturListUiStore: AgenturListUiStore;
	@observable
	agtUserUiStore: AgtUserUiStore;
	constructor(session: SessionStore, mapsStore: MapsStore, agenturListUiStore: AgenturListUiStore, agtUserUiStore: AgtUserUiStore) {
		makeObservable(this);
		this.session = session;
		this.mapsStore = mapsStore;
		this.agenturListUiStore = agenturListUiStore;
		this.agtUserUiStore = agtUserUiStore;
	}

	@observable
	higlightedAgtId?: number;

	@observable
	selectedAgt?: AgenturModel;

	@computed
	get legendOpts(): BaseMapLegend[] {
		if (!this.agenturListUiStore || !this.agenturListUiStore.currentFilter.gsOpts) {
			return [];
		}
		return [
			DefaultLegend,
			new GsMapLegend(this.agenturListUiStore.currentFilter.gsOpts),
			new SegmentierungMapLegend(this.agenturListUiStore.currentFilter.segmentOpts),
			new EdpMapLegend(this.edpOpts),
		];
	}

	@observable
	currentLegend: BaseMapLegend = DefaultLegend;

	@computed
	get mapableItems() {
		return this.agenturListUiStore.items.filter((x) => x.agtType === AgtType.agt);
	}

	@computed
	get mapableItemsWithoutCoordinates() {
		return this.mapableItems.filter((x) => x.lat === 0 || x.lng === 0);
	}

	@computed
	get filteredMapItems() {
		return this.agenturListUiStore.filteredItems.filter((x) => x.agtType === AgtType.agt);
	}

	@computed
	get filtereGroupedMapItems() {
		const items = this.agenturListUiStore.filteredItems.reduce((grouped, item) => {
			const key = `${item.lat},${item.lng}`;
			if (!grouped[key]) {
				grouped[key] = [];
			}
			grouped[key].push(item);
			return grouped;
		}, {} as { [key: string]: AgenturModel[] });
		const keys = _.keys(items);
		const res: GroupedAgtVM[] = [];
		keys.forEach((k) => {
			const i = items[k];
			res.push(new GroupedAgtVM(i));
		});
		return res;
	}

	@computed
	get edpOpts() {
		let edps = this.agtUserUiStore.items.filter((u) => u.isEDP);
		edps = _.sortBy(edps);
		return edps.map((i) => {
			return {
				label: i.lastName,
				value: i.bnrId.toString(),
			};
		});
	}

	getNumberMarker(number: string, color?: string) {
		const colorStr = color ? color : mapRedFill;
		const svg = `<svg width="26" height="35" viewBox="0 0 26 35" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M12.0675 34.0142L12.0626 34.0066L12.0575 33.9992C10.4619 31.6842 9.08054 29.7074 7.88538 27.997C5.36668 24.3925 3.67481 21.9712 2.54628 20.0626C1.72038 18.6658 1.21366 17.571 0.910149 16.5128C0.607527 15.4576 0.5 14.4145 0.5 13.1122C0.5 6.14355 6.03708 0.5 13 0.5C19.8969 0.5 25.5 6.14525 25.5 13.1122C25.5 14.4132 25.3885 15.4556 25.08 16.5102C24.7705 17.5684 24.2556 18.6633 23.421 20.0607C22.3202 21.9038 20.6951 24.2235 18.3129 27.624C17.0558 29.4185 15.5878 31.514 13.8748 33.9992L13.8697 34.0066L13.8648 34.0142C13.452 34.6619 12.4803 34.6619 12.0675 34.0142Z" 
		fill="${colorStr}" stroke="#000000" stroke-opacity="0.2"/>		
		<text x="50%" y="40%" text-anchor="middle" fill="#111111" opacity="0.5" font-size="14px" font-family="Arial" font-weight="bold" dy=".3em">${number}</text>
		</svg>`;

		return svg;
	}

	@computed
	get agenturSeries() {
		const dvsData: MapPoint[] = [];
		for (const vm of this.agenturListUiStore.filteredItems) {
			const p = new MapPoint(vm, 0);
			if (p.hasCoordinates) {
				dvsData.push(p);
			}
		}
		const _series: DvsSeriesOption = {
			name: 'DVS Agenturen',
			type: 'scatter',
			coordinateSystem: 'gmap',
			dvsData,
			color: 'red',
			symbol: 'pin',
			zlevel: 1,
			symbolSize: 40,
			showAgtLabel: true,
		};

		return _series;
	}

	getAgtsByLngLat(lng: number, lat: number): AgenturModel[] {
		const agts = this.agenturListUiStore.items.filter((a) => a.lng === lng && a.lat === lat);
		return agts;
	}

	@observable
	activeGroup?: GroupedAgtVM;

	@observable
	activeLngLat?: number[];
	@observable
	activeAgts: AgenturModel[] = [];

	async closeActiveAgts() {
		this.activeAgts = [];
		this.activeLngLat = [];
	}

	currentOpenInfoWindow: any = null;
	@action
	async openActiveAgts(map: any, lngLat: number[]) {
		this.activeLngLat = lngLat;
		if (!lngLat) {
			return;
		}
		const lng = lngLat[0];
		const lat = lngLat[1];
		const agts = this.agenturListUiStore.items.filter((a) => a.lng === lng && a.lat === lat);
		this.activeAgts = agts;
		const infowindow = new google.maps.InfoWindow({
			content: '',
			zIndex: 9999999,
		});

		const m = new google.maps.marker.AdvancedMarkerElement({
			position: { lat: lat, lng },
			map: map,
			// title,
			// content: pin,
		});
		// m.hidden = true;

		await new Promise((res) => setTimeout(res, 100));
		const infowindowContent = document.getElementById('infowindowcontent');

		if (infowindowContent) {
			const htmlContent = infowindowContent.innerHTML;
			// const htmlContent = "Hello world"
			infowindow.setContent(htmlContent);
			if (this.currentOpenInfoWindow) {
				this.currentOpenInfoWindow.close();
			}

			infowindow.open({
				anchor: m,
				map,
			});
			// infowindow.setZIndex(9999999);

			// Step 3: Update the currently opened InfoWindow to the new one
			this.currentOpenInfoWindow = infowindow;
			this.currentOpenInfoWindow.setZIndex(9999999);
			this.currentOpenInfoWindow.addListener('idle', () => {
				this.currentOpenInfoWindow.focus();
				// this.closeActiveAgts();
			});
		}
	}
}

/*
Google Maps zoom level and coordinates:

- The map uses latitude/longitude coordinates.
- The zoom level determines the scale of the map:
	- At zoom 0, the entire world fits into a 256x256 pixel tile.
	- Each increment in zoom level doubles the scale (i.e., zoom 1 = 512x512, zoom 2 = 1024x1024, etc).
	- Formula for meters per pixel at latitude φ:
		metersPerPixel = 156543.03392 * cos(latitude * Math.PI / 180) / Math.pow(2, zoom)
	- At higher zoom levels, the map shows a smaller area in more detail.

- Coordinates themselves do not change with zoom, but the number of pixels representing a given distance increases as you zoom in.
- To convert between lat/lng and pixel coordinates at a given zoom, use Google Maps projection utilities (e.g., fromLatLngToPoint).

References:
- https://developers.google.com/maps/documentation/javascript/overview#zoom-levels
- https://developers.google.com/maps/documentation/javascript/coordinates
*/

/*
To group coordinates by proximity when changing the zoom level:

1. Decide on a proximity threshold (in meters or pixels) that depends on the zoom level.
2. For each point, check if it is within the threshold of an existing group; if so, add it to that group, otherwise start a new group.
3. The threshold can be calculated using the meters-per-pixel formula for the current zoom and latitude.

Example approach:
- For each point, convert its lat/lng to pixel coordinates at the current zoom (using Google Maps projection).
- Use a clustering algorithm (e.g., grid-based, DBSCAN, or simple distance check) to group points whose pixel distance is below a threshold.
- The threshold can be set so that clusters are visually meaningful at the current zoom.

References:
- https://developers.google.com/maps/documentation/javascript/marker-clustering
- https://developers.google.com/maps/documentation/javascript/coordinates#pixel-xy

Example pseudo-code:
```
const thresholdPx = 40; // e.g., 40 pixels const projection = map.getProjection(); const zoom = map.getZoom(); const groups = [];

for (const point of points) { 
	const pixel = projection.fromLatLngToPoint(new google.maps.LatLng(point.lat, point.lng)); 
	let added = false; 
	for (const group of groups) { 
		const groupPixel = projection.fromLatLngToPoint(new google.maps.LatLng(group.lat, group.lng)); 
		const dx = pixel.x - groupPixel.x; const dy = pixel.y - groupPixel.y; 
		const distPx = Math.sqrt(dxdx + dydy) * Math.pow(2, zoom); if (distPx < thresholdPx) { group.points.push(point); added = true; break; } } if (!added) { groups.push({ lat: point.lat, lng: point.lng, points: [point] }); } }


*/
